import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import {
  Avatar,
  MenuItem,
  Select as MuiSelect,
  type SelectChangeEvent,
  styled,
} from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import _ from 'lodash';
import { ChevronLeft, Plus } from 'lucide-react';

import Button from '../common/Button';
import { type TypeMember, type TypeProject } from '../common/dataTypes';
import {
  createTanstackTable,
  OrganizationRolesOptions,
  ProjectRolesOptions,
  toastPromise,
} from '../common/utils';
import {
  useGetOrgMemberById,
  useUpdateOrganizationMemberRole,
} from '../hooks/tanstack/useOrganizationHook';
import {
  useGetProjectById,
  useUpdateProjectMember,
} from '../hooks/tanstack/useProjectsHook';
import { useGetUser } from '../hooks/tanstack/useUserHook';
import {
  isOrgAdmin,
  isOrgSuperAdmin,
  useCustomNavigate,
  useGetRouteParams,
} from '../hooks/tanstack/useUtilsHook';
import routes from '../routes';
import { type OrganizationRole, type Role } from '../types';
import cn from '../utils/cn';
import {
  transformToLabel,
  useCurrentMembership,
  useCurrentOrganization,
  useGetCurrentMember,
} from '../utils/helpers';

import FormSelect from './form/FormSelect';
import TransferOwnershipDialog from './organizations/TransferOwnershipDialog';
import InviteProjectFormDialog from './projects/InviteProjectFormDialog';
import ConfirmDeleteUserDialog from './userManagement/ConfirmDeleteUserDialog';
import ConfirmLeaveProjectDialog from './userManagement/ConfirmLeaveProjectDialog';
import ConfirmTransferDialog from './ConfirmTransferDialog';
import TanstackTable from './TanstackTable';

export type DataRow = {
  id: number;
  name: string;
  lastActive: string | null;
  role: 'project_member' | 'project_owner' | 'Unknown';
};

export type User = {
  id: number;
  userId: number;
  role: OrganizationRole;
  first_name?: string;
  last_name?: string;
  profile_pic?: string;
  updated_at: string;
  email: string;
  projects: TypeProject[];
};

const columnHelper = createColumnHelper<DataRow>();

const Select = styled(MuiSelect)({
  padding: '0px !important',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
    padding: '0px !important',
  },

  '& .MuiSelect-select': {
    backgroundColor: '#F8F8F8',
    paddingLeft: '10px !important',
    paddingRight: '60px !important',
  },
  '& .MuiSelect-nativeInput': {},
});

const columns = (user: User) => [
  columnHelper.accessor('name', {
    cell: (info) => (
      <div className="group flex items-center gap-2 text-start">
        <p className="w-full line-clamp-2">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="items-centert flex gap-2">
        <p>Project</p>
      </div>
    ),
  }),
  columnHelper.accessor('lastActive', {
    id: 'lastActive',
    cell: (info) => {
      const value = info.row.original.lastActive;

      return (
        <div className="w-full">
          {value ? format(info.getValue() as string, 'y/MM/dd h:mm a') : '--'}
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.lastActive as string).getTime() -
      new Date(b.original.lastActive as string).getTime(),
    header: () => <div>Last active</div>,
  }),
  columnHelper.accessor('role', {
    id: 'role',
    cell: (info) => {
      const currentRole = info.getValue();
      const project = info.row.original;

      const { userId, orgId } = useGetRouteParams();

      const [role, setRole] = useState<Role>(currentRole);
      const { data: currentUser } = useGetUser();

      const { data: currentOrg } = useCurrentOrganization();

      const { data: orgMember } = useGetOrgMemberById(
        {
          orgId,
          memberId: userId,
        },
        {
          enabled: !!userId && !!orgId,
        }
      );

      const projectMember = orgMember?.project_memberships.find(
        (p) => p.project.id === project.id
      );

      useEffect(() => {
        if (currentRole) {
          setRole(currentRole);
        }
      }, [currentRole]);

      const { mutate: updateProjectMember } = useUpdateProjectMember();

      if (!info.getValue()) {
        return <div className="text-gray-400 w-full text-start">--</div>;
      }

      const handleChangeRole = (event: SelectChangeEvent<unknown>) => {
        if (!currentOrg) {
          return;
        }

        const selectedRole = event.target.value as Role;

        if (projectMember) {
          updateProjectMember({
            orgId,
            projectId: project.id,
            memberId: projectMember.id,
            data: {
              role: selectedRole,
            },
          });
        }

        setRole(selectedRole);
      };

      const isAllowEdit =
        currentOrg && currentUser?.id !== user.userId
          ? (isOrgAdmin(currentOrg.role) && !isOrgSuperAdmin(user.role)) ||
            isOrgSuperAdmin(currentOrg.role)
          : false;

      const isAuthenticated =
        currentUser?.id !== user.userId
          ? currentOrg &&
            (isOrgSuperAdmin(currentOrg.role) ||
              (isOrgAdmin(currentOrg.role) && !isOrgSuperAdmin(user.role)))
          : true;

      return (
        <div className="w-full flex justify-between">
          <div className="max-w-[200px] w-full capitalize">
            {!isAllowEdit ? (
              <div className="bg-[#F8F8F8] px-4 py-1 rounded-[4px] text-[#333333]">
                {ProjectRolesOptions.find((p) => p.value === role)?.label}
              </div>
            ) : (
              <Select
                id="role"
                size="small"
                value={role}
                fullWidth
                onChange={handleChangeRole}
                className="bottom-0"
                placeholder="Please select"
              >
                {ProjectRolesOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <span className="capitalize">{option.label}</span>
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
          {projectMember && orgMember && isAuthenticated && (
            <ConfirmLeaveProjectDialog
              project={project}
              user={{
                id: projectMember.id,
                email: orgMember.user.email,
                type: 'member',
              }}
            />
          )}
        </div>
      );
    },
    header: () => <div>Project roles</div>,
  }),
];

const UserAccountDetails = () => {
  const [data, setData] = useState<DataRow[]>([]);
  const [selectedMember, setSelectedMember] = useState<TypeMember | null>(null);
  const [openInviteFormDialog, setOpenInviteFormDialog] = useState(false);

  const [openConfirmTransferDialog, setOpenConfirmTransferDialog] =
    useState(false);

  const [openTransferOwnershipDialog, setOpenTransferOwnershipDialog] =
    useState(false);

  const navigate = useCustomNavigate();

  const { userId, orgId, projectId } = useGetRouteParams();

  const { data: currentUser } = useGetUser();

  const currentMember = useGetCurrentMember();

  const { data: project } = useGetProjectById(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const {
    data: member,
    isLoading: isFetchingOrgMember,
    isRefetching: isRefetchingOrgMember,
  } = useGetOrgMemberById(
    {
      orgId,
      memberId: userId,
    },
    {
      enabled: !!userId && !!orgId,
    }
  );

  const {
    mutateAsync: updateProjectMemberRole,
    isPending: isUpdatingOrgMemberRole,
  } = useUpdateOrganizationMemberRole();

  const user = useMemo(
    () =>
      member &&
      ({
        ..._.omit(member.user, ['id', 'role', 'projects']),
        id: member.id,
        userId: member.user.id,
        role: member.role,
        projects: member.project_memberships
          .filter((p) =>
            currentMember?.project_memberships.some(
              (pm) => pm.project.id === p.project.id
            )
          )
          .map((p) => ({
            ...p.project,
            id: p.project.id,
            role: p.role,
          })),
        updated_at: member.updated_at,
      } as User),
    [member]
  );

  const form = useForm({
    defaultValues: {
      role: user?.role ?? 'organization_admin',
    },
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (user) {
      setData(
        user.projects.map((project) => ({
          id: project.id,
          name: project.name,
          lastActive: project.updated_at,
          role: project.role,
        }))
      );
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      reset({
        role: user.role,
      });
    }
  }, [user]);

  const table = createTanstackTable<DataRow>({
    data,
    columns: columns(user ?? (member as unknown as User)),
  });

  const handleGoBack = () => {
    navigate(routes.userManagement.index, { org_id: orgId.toString() });
  };

  const handleOpenInviteFormDialog = () => {
    setOpenInviteFormDialog(true);
  };

  const handleCloseInviteFormDialog = () => {
    setOpenInviteFormDialog(false);
  };

  const handleOpenTransferOwnershipDialog = () => {
    setOpenTransferOwnershipDialog(true);
  };

  const handleCloseTransferOwnershipDialog = () => {
    setOpenTransferOwnershipDialog(false);
  };

  const handleOpenConfirmTransferDialog = () => {
    setOpenConfirmTransferDialog(true);
  };

  const handleCloseConfirmTransferDialog = () => {
    setOpenConfirmTransferDialog(false);
  };

  const { data: currentOrg } = useCurrentOrganization();

  const { organization: currentOrgMembership } = useCurrentMembership();

  const isUsing = user && currentUser && currentUser.id === user.userId;

  const isAllowEdit =
    currentOrg && user && !isUsing
      ? (isOrgAdmin(currentOrg.role) &&
          !isOrgSuperAdmin(user.role) &&
          !isOrgAdmin(user.role)) ||
        isOrgSuperAdmin(currentOrg.role)
      : false;

  const isAuthenticated = currentOrg?.role !== 'organization_member';

  const handleSubmitUpdateRole: SubmitHandler<{
    role: OrganizationRole;
  }> = async (data) => {
    const { role } = data;

    const res = updateProjectMemberRole({
      orgId,
      memberId: userId,
      data: {
        role,
        organization: orgId,
      },
    });

    toastPromise({
      promise: res,
      content: `${user?.first_name + ' ' + user?.last_name} role updated to ${transformToLabel(role)}`,
    });
  };

  return (
    <div className="mx-auto flex max-w-lg 3xl:max-w-2xl flex-col gap-4 py-6">
      <button onClick={handleGoBack} className="flex items-center gap-2">
        <ChevronLeft />
        {user && (
          <p className="text-[28px] font-bold">
            {`${user.first_name} ${user.last_name}`}
          </p>
        )}
        <p
          className={cn(
            'flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase',
            {
              'bg-[#FFE8B9]': user?.role === 'organization_admin',
              'bg-[#E8F2FF]': user?.role === 'organization_owner',
              'bg-[#F5F5F5]': user?.role === 'organization_member',
            }
          )}
        >
          {user &&
            OrganizationRolesOptions.find((p) => p.value === user.role)?.label}
        </p>
      </button>
      <div className="flex gap-6">
        <div className="bg-white w-full space-y-3 pt-2">
          {user?.updated_at && (
            <p className="text-sm font-semibold text-[#666]">
              Last active on{' '}
              {format(user?.updated_at ?? ('' as string), 'y/MM/dd h:mm a')}
            </p>
          )}
          <div className="flex w-full justify-between">
            <div className="flex flex-col gap-3 rounded-[4px] border border-[#DBE7F6] p-4">
              <div className="flex gap-3">
                <Avatar
                  src={user?.profile_pic}
                  sx={{
                    width: 70,
                    height: 70,
                  }}
                  alt="member avatar"
                />
                <div className="flex flex-col justify-center">
                  <p className="font-semibold">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-sm">{user?.email}</p>
                </div>
              </div>
              {user?.role === 'organization_owner' &&
                user.userId === currentUser?.id && (
                  <div className="w-full">
                    <Button
                      onClick={handleOpenTransferOwnershipDialog}
                      variant="outlined_red"
                      className="w-full"
                    >
                      Transfer ownership
                    </Button>
                    <TransferOwnershipDialog
                      open={openTransferOwnershipDialog}
                      handleCancelDialog={handleCloseTransferOwnershipDialog}
                      handleSubmitDialog={(member) => {
                        setSelectedMember(member);
                        handleCloseTransferOwnershipDialog();
                        handleOpenConfirmTransferDialog();
                      }}
                    />
                  </div>
                )}
              {(user?.role === 'organization_member' ||
                (user?.role === 'organization_admin' &&
                  currentOrgMembership.role === 'organization_owner')) &&
                user.userId !== currentUser?.id &&
                currentOrgMembership.role !== 'organization_member' && (
                  <FormProvider {...form}>
                    <form
                      onSubmit={handleSubmit(handleSubmitUpdateRole)}
                      className="w-full flex items-center gap-2"
                    >
                      <span className="font-semibold">Role: </span>
                      <FormSelect
                        name="role"
                        defaultValue={user.role}
                        fullWidth
                        options={
                          [
                            {
                              value: 'organization_admin',
                              label: 'Organization Admin',
                            },
                            {
                              value: 'organization_member',
                              label: 'Organization Member',
                            },
                          ] as {
                            value: OrganizationRole;
                            label: string;
                          }[]
                        }
                      />
                      <Button
                        type="submit"
                        loading={isUpdatingOrgMemberRole}
                        onClick={handleOpenTransferOwnershipDialog}
                        variant="contained_blue"
                      >
                        Save
                      </Button>
                    </form>
                  </FormProvider>
                )}
            </div>
            <div>
              {' '}
              {user && isAllowEdit && isAuthenticated ? (
                <ConfirmDeleteUserDialog
                  trigger={{
                    className: 'text-primary-red',
                  }}
                  user={user}
                  level={'organization'}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div />
      </div>
      <div className="flex grow flex-col gap-3">
        <div className="flex justify-between items-center">
          <p className="text-xl font-semibold">Project access</p>
          {user &&
            user.userId !== currentUser?.id &&
            isAuthenticated &&
            project && (
              <InviteProjectFormDialog
                project={project}
                iUser={{
                  id: user.userId,
                  email: user.email,
                  fullName: `${user.first_name} ${user.last_name}`,
                }}
                inviteUserMode="single"
                trigger={{
                  label: (
                    <div className="flex gap-1 items-center">
                      <Plus size={20} />
                      <p>Add to project(s)</p>
                    </div>
                  ),
                }}
                open={openInviteFormDialog}
                handleClose={handleCloseInviteFormDialog}
                handleOpen={handleOpenInviteFormDialog}
                projectMembers={[]}
              />
            )}
        </div>
        <TanstackTable
          table={table}
          isLoading={isFetchingOrgMember || isRefetchingOrgMember}
          emptyState={
            isUsing && currentOrg?.role === 'organization_member'
              ? 'You haven’t been invited to work in any projects yet'
              : 'No available projects.'
          }
        />
      </div>
      {selectedMember && (
        <ConfirmTransferDialog
          open={openConfirmTransferDialog}
          handleClose={handleCloseConfirmTransferDialog}
          user={{
            id: selectedMember.user.id,
            name: selectedMember.user.full_name,
            email: selectedMember.user.email,
          }}
        />
      )}
    </div>
  );
};

export default UserAccountDetails;
